'use client'

import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { createBeneficiary, updateBeneficiary, type Beneficiary, type Country } from '@/lib/supabase-queries'

interface BeneficiaryModalProps {
  beneficiary: Beneficiary | null
  isEditing: boolean
  countries: Country[]
  onClose: () => void
  onSave: () => void
}

export default function BeneficiaryModal({ 
  beneficiary, 
  isEditing, 
  countries, 
  onClose, 
  onSave 
}: BeneficiaryModalProps) {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    document_type: 'CC',
    document_number: '',
    beneficiary_type: 'Natural Person',
    country_id: '',
    bank_name: '',
    account_type: 'Savings',
    account_number: ''
  })

  // Initialize form data only when modal opens or beneficiary changes
  useEffect(() => {
    if (beneficiary && isEditing) {
      setFormData({
        name: beneficiary.name,
        document_type: beneficiary.document_type,
        document_number: beneficiary.document_number,
        beneficiary_type: beneficiary.beneficiary_type,
        country_id: beneficiary.country_id,
        bank_name: beneficiary.bank_name,
        account_type: beneficiary.account_type,
        account_number: beneficiary.account_number
      })
    } else {
      // Set default country to USA
      const usaCountry = countries.find(c => c.code === 'USA')
      setFormData({
        name: '',
        document_type: 'CC',
        document_number: '',
        beneficiary_type: 'Natural Person',
        country_id: usaCountry?.id || '',
        bank_name: '',
        account_type: 'Savings',
        account_number: ''
      })
    }
  }, [beneficiary?.id, isEditing]) // Only depend on beneficiary ID and editing state, not the full countries array

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return

    setLoading(true)
    try {
      if (isEditing && beneficiary) {
        await updateBeneficiary(beneficiary.id, formData)
      } else {
        await createBeneficiary({
          ...formData,
          user_id: user.id
        })
      }
      onSave()
    } catch (error) {
      console.error('Error saving beneficiary:', error)
      alert('Error saving beneficiary. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  return (
    <div className="fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Beneficiary' : 'New Beneficiary'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    placeholder="Enter full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document Type *
                  </label>
                  <select
                    value={formData.document_type}
                    onChange={(e) => handleInputChange('document_type', e.target.value)}
                    className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="CC">CC - Citizenship Card</option>
                    <option value="NIT">NIT - Tax Identification Number</option>
                    <option value="PAS">PAS - Passport</option>
                    <option value="CE">CE - Foreign ID</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Document Number *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.document_number}
                    onChange={(e) => handleInputChange('document_number', e.target.value)}
                    className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    placeholder="Enter document number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Beneficiary Type *
                  </label>
                  <select
                    value={formData.beneficiary_type}
                    onChange={(e) => handleInputChange('beneficiary_type', e.target.value)}
                    className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="Natural Person">Natural Person</option>
                    <option value="Legal Entity">Legal Entity</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Location Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country *
                </label>
                <select
                  value={formData.country_id}
                  onChange={(e) => handleInputChange('country_id', e.target.value)}
                  className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                >
                  <option value="" className="text-gray-500">Select country</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id} className="text-gray-900">
                      {country.flag_emoji} {country.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Bank Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Bank Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bank Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.bank_name}
                    onChange={(e) => handleInputChange('bank_name', e.target.value)}
                    className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    placeholder="Enter bank name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Type *
                  </label>
                  <select
                    value={formData.account_type}
                    onChange={(e) => handleInputChange('account_type', e.target.value)}
                    className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="Savings">Savings</option>
                    <option value="Checking">Checking</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Number *
                </label>
                <input
                  type="text"
                  required
                  value={formData.account_number}
                  onChange={(e) => handleInputChange('account_number', e.target.value)}
                  className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  placeholder="Enter account number"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : (isEditing ? 'Update Beneficiary' : 'Create Beneficiary')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
